import { css } from 'aphrodite';
import * as React from 'react';
import { NavLink, Route, Switch, useParams, useRouteMatch } from 'react-router-dom';
import { IImpersonationContext } from '../../../extViewmodels';
import * as AdminViewModels from '../../../viewmodels/AdminViewModels';
import {
	AdminDrawerToolbar,
	AppDrawerToolbarButton,
	SideDrawer,
	StickyAppDrawerContent,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { ScheduleMeetingIcon } from '../../../web/components/svgs/icons/ScheduleMeetingIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import {
	AdminAppAssistantChat,
	SuccessMeetingPrepAppAssistantChat,
} from '../../../web/containers/templates/AssistantChat';
import { ConditionalAccess } from '../../components/ConditionalAccess';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { AccountDetailsTabContentOverview } from './AccountDetailsTabContentOverview';
import { AccountDetailsTabContentWebsite } from './AccountDetailsTabContentWebsite';
import { styleSheet } from './styles';

const ACCOUNT_TABS = [
	{ label: 'Account Overview', path: '' },
	{ label: 'Website', path: 'website' },
	{ label: 'Content Preferences', path: 'content-preferences' },
	{ label: 'Ads', path: 'ads' },
	{ label: 'Users', path: 'users' },
	{ label: 'Imports', path: 'imports' },
	{ label: 'Texting', path: 'texting' },
	{ label: 'Tags', path: 'tags' },
	{ label: 'Group Emails', path: 'group-emails' },
	{ label: 'Automations', path: 'automations' },
	{ label: 'Social', path: 'social' },
	{ label: 'Integrations', path: 'integrations' },
	{ label: 'Connection Map', path: 'connection-map' },
	{ label: 'Settings', path: 'settings' },
] as const;

export const AdminAccountDetailsV2Page: React.FC = () => {
	const params = useParams<{ accountId: string }>();
	const { path, url } = useRouteMatch();
	const impersonationContext: IImpersonationContext = { account: { id: params.accountId } };

	return (
		<AdminDashboardPageLayout
			contentStyles={[styleSheet.content]}
			sidebarChildren={
				<SideDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<AdminAppAssistantChat impersonationContext={impersonationContext} />
					</StickyAppDrawerContent>
					<StickyAppDrawerContent value='success-meeting-prep'>
						<SuccessMeetingPrepAppAssistantChat impersonationContext={impersonationContext} />
					</StickyAppDrawerContent>
					<AdminDrawerToolbar>
						<AppDrawerToolbarButton value='ai-assistant' tooltip='AI Assistant'>
							<SparklesIcon />
						</AppDrawerToolbarButton>
						<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount}>
							<AppDrawerToolbarButton value='success-meeting-prep' tooltip='Prepare for Meeting'>
								<ScheduleMeetingIcon fill='currentColor' />
							</AppDrawerToolbarButton>
						</ConditionalAccess>
					</AdminDrawerToolbar>
				</SideDrawer>
			}
		>
			<div className={css(styleSheet.container)}>
				<nav className={css(styleSheet.verticalTabList)}>
					{ACCOUNT_TABS.map(tab => (
						<NavLink
							key={tab.path ?? '/'}
							to={tab.path ? `${url.replace(/\/$/, '')}/${tab.path}` : url}
							exact={!tab.path}
							className={css(styleSheet.tabTrigger)}
							activeClassName={css(styleSheet.tabTriggerActive)}
						>
							{tab.label}
						</NavLink>
					))}
				</nav>
				<div className={css(styleSheet.contentArea)}>
					<Switch>
						<Route exact path={path}>
							<AccountDetailsTabContentOverview />
						</Route>
						<Route path={`${path}/website`}>
							<AccountDetailsTabContentWebsite />
						</Route>
						<Route path={`${path}/content-preferences`}>
							<PlaceholderTabContent tabName='Content Preferences' />
						</Route>
						<Route path={`${path}/ads`}>
							<PlaceholderTabContent tabName='Ads' />
						</Route>
						<Route path={`${path}/users`}>
							<PlaceholderTabContent tabName='Users' />
						</Route>
						<Route path={`${path}/imports`}>
							<PlaceholderTabContent tabName='Imports' />
						</Route>
						<Route path={`${path}/texting`}>
							<PlaceholderTabContent tabName='Texting' />
						</Route>
						<Route path={`${path}/tags`}>
							<PlaceholderTabContent tabName='Tags' />
						</Route>
						<Route path={`${path}/group-emails`}>
							<PlaceholderTabContent tabName='Group Emails' />
						</Route>
						<Route path={`${path}/automations`}>
							<PlaceholderTabContent tabName='Automations' />
						</Route>
						<Route path={`${path}/social`}>
							<PlaceholderTabContent tabName='Social' />
						</Route>
						<Route path={`${path}/integrations`}>
							<PlaceholderTabContent tabName='Integrations' />
						</Route>
						<Route path={`${path}/connection-map`}>
							<PlaceholderTabContent tabName='Connection Map' />
						</Route>
						<Route path={`${path}/settings`}>
							<PlaceholderTabContent tabName='Settings' />
						</Route>
					</Switch>
				</div>
			</div>
		</AdminDashboardPageLayout>
	);
};

const PlaceholderTabContent = ({ tabName }: { tabName: string }) => {
	return <div className={css(styleSheet.placeholderContent)}>Placeholder for {tabName} tab</div>;
};
